import { Text } from '@genial/components';
import { BoxRadius, Color, Spacing } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import React, { useMemo } from 'react';
import { View } from 'react-native';

import { AccountsSelectionMenu } from './components/AccountsSelectionMenu';
import { AccountsSelectionModal } from './components/AccountsSelectionModal';
import { MultipleAccountsSelectionProps } from './types';

const emptyValue = '--';

const getAccountForDisplay = (value?: string) => {
  return value || emptyValue;
};

const mobileWrapperStyle = {
  flex: 1,
  padding: Utils.Token.ToInt(Spacing, 'inset.nano'),
  backgroundColor: Color.background,
  borderRadius: Utils.Token.ToInt(BoxRadius, 'sm'),
};

export const MultipleAccountsSelection: React.FC<MultipleAccountsSelectionProps> = ({
  accountSelected,
  setAccountSelected,
  accounts,
  isMobile,
}) => {
  const isTabletOrMobile = useBreakpointValue({ base: true, sm: true, lg: false }) as boolean;
  const selectedAccount = useMemo(() => {
    return accountSelected || accounts[0];
  }, [accountSelected, accounts]);

  const normalizedSelectedAccount = useMemo(
    () => ({
      ...selectedAccount,
      custodyAccountNormalized: getAccountForDisplay(selectedAccount?.custodyAccountNormalized),
      sinacorAccountNormalized: getAccountForDisplay(selectedAccount?.sinacorAccountNormalized),
    }),
    [selectedAccount]
  );

  if (!selectedAccount) {
    return null;
  }

  if (accounts.length > 1 && setAccountSelected) {
    if (isTabletOrMobile) {
      const content = (
        <AccountsSelectionModal
          accounts={accounts.map((account) => ({
            ...account,
            custodyAccountNormalized: getAccountForDisplay(account.custodyAccountNormalized),
            sinacorAccountNormalized: getAccountForDisplay(account.sinacorAccountNormalized),
          }))}
          selectedAccount={normalizedSelectedAccount}
          onSelect={setAccountSelected}
        />
      );

      if (isMobile) {
        return <View style={mobileWrapperStyle}>{content}</View>;
      }

      return content;
    }

    const menu = (
      <AccountsSelectionMenu
        accounts={accounts.map((account) => ({
          ...account,
          custodyAccountNormalized: getAccountForDisplay(account.custodyAccountNormalized),
          sinacorAccountNormalized: getAccountForDisplay(account.sinacorAccountNormalized),
        }))}
        selectedAccount={normalizedSelectedAccount}
        onSelect={setAccountSelected}
      />
    );

    if (isMobile) {
      return <View style={mobileWrapperStyle}>{menu}</View>;
    }

    return menu;
  }

  return (
    <Text color="neutral.black">
      {getAccountForDisplay(selectedAccount.custodyAccountNormalized)}
    </Text>
  );
};
